import * as z from "zod";
import type { InjectionKey } from "vue";

import { LSDateSchema, LSDateTimeSchema } from "#shared/schemas/datetime";
import type { LSSuburb } from "#shared/types/suburb";

// The place-type union (formerly LSWaypointType) moved to the shared nuxt-library
// waypoint core as WaypointPlaceType; import it from '#imports'.

// Suburb lookup is provided by the consuming app (which owns the authenticated $api),
// then injected into the base-library waypoint component. See web-portals plugin.
export type WaypointGetSuburbs = (localityID: string, suburbPartial: string) => Promise<LSSuburb[]>;
export const WAYPOINT_GET_SUBURBS = Symbol("waypoint:getSuburbs") as InjectionKey<WaypointGetSuburbs>;

export const BaseLSWaypointSchema = z.object({
	locationID: z.string().optional().default(""),

	address: z.string("Invalid address field"),
	address1: z.string().optional().default(""),
	address2: z.string().optional().default(""),

	name: z.string().optional().default(""),
	suburb: z.string("Invalid suburb field"),
	state: z.string("Invalid state field"),
	postCode: z.string("Invalid postcode field"),
	region: z.string().optional().default(""),

	fromDate: LSDateSchema,
	fromTime: LSDateTimeSchema,
	toTime: LSDateTimeSchema.optional().default(""),

	lat: z.string("Invalid latitude field").default(""),
	lng: z.string("Invalid longitude field").default(""),

	phone: z.string().optional().default(""),
	passengers: z.string().optional().default(""),
	contact: z.string().optional().default(""),

	flight: z.string().optional().default(""),
	notes: z.string().optional().default(""),
	instructions: z.string().optional().default(""),
});
export const BaseLSWaypointSchemaPartial = BaseLSWaypointSchema.partial();

export const LSWaypointSchema = BaseLSWaypointSchema.transform((data) => {
	const fromDate = data.fromDate.split("T")[0] || "";
	const fromTime = data.fromTime.split("T")[1] || "00:00:00";
	const newFromTime = `${fromDate}T${fromTime}`;

	const toDate = data.toTime ? data.toTime.split("T")[0] : "";
	const toTime = data.toTime ? data.toTime.split("T")[1] || "00:00:00" : "00:00:00";
	const newToTime = toDate ? `${toDate}T${toTime}` : "";

	return {
		...data,
		fromTime: newFromTime,
		toTime: newToTime,
	};
});
export type LSWaypoint = z.infer<typeof BaseLSWaypointSchema>;

export const LSWaypointPartialSchema = BaseLSWaypointSchema.partial().transform((data) => {
	const fromDate = data.fromDate ? data.fromDate.split("T")[0] || "" : "";
	const fromTime = data.fromTime ? data.fromTime.split("T")[1] || "00:00:00" : "00:00:00";
	const newFromTime = `${fromDate}T${fromTime}`;

	const toDate = data.toTime ? data.toTime.split("T")[0] : "";
	const toTime = data.toTime ? data.toTime.split("T")[1] || "00:00:00" : "00:00:00";
	const newToTime = toDate ? `${toDate}T${toTime}` : "";

	return {
		...data,
		fromTime: newFromTime,
		toTime: newToTime,
	};
});
export type LSWaypointPartial = z.infer<typeof BaseLSWaypointSchemaPartial>;
export type PartialIndexedJobWaypoint = LSWaypointPartial & { id?: string; showDetails?: boolean; hotelsOnly?: boolean; airportsOnly?: boolean };

export const AIRPORT_FLIGHT_REQUIRED_MESSAGE = "A flight number is required when the address is an airport.";

// A waypoint flagged as an airport must carry a flight number. `airportsOnly` is
// the transient airport signal set when an airport address is selected (see
// useLSWaypoint.formatSelectedLocation) or the airport type is chosen; `flight`
// is the number the customer must supply. This is the single rule shared by the
// address-step UI, the guided-flow step guard, and the create-job submit schema
// so a booking can never leave with an airport leg and a blank flight number.
// Fields are typed `unknown` so this stays assignment-clean when called on a
// `.loose()` schema output (where extra props widen to unknown) as well as on a
// strongly-typed waypoint; the runtime checks are defensive either way.
export function isAirportWaypointMissingFlight(waypoint: { airportsOnly?: unknown; flight?: unknown } | null | undefined): boolean {
	return waypoint?.airportsOnly === true && String(waypoint.flight ?? "").trim() === "";
}
